import React, { useState } from "react";
import { Button, Text } from "rizzui";
import { PiCaretLeftBold, PiCaretRightBold, PiXBold } from "react-icons/pi";
import { useModal } from "@/app/shared/modal-views/use-modal";

interface Step {
  id: string;
  title: string;
  description: string;
  fields: { label: string; help: string }[];
  tip?: string;
}

const STEPS: Step[] = [
  {
    id: "bsd",
    title: "Buyer Stamp Duty (BSD)",
    description:
      "BSD is a tax paid by buyers on the purchase price of any residential or non-residential property in Singapore. Rates are tiered — the higher the price, the higher the marginal rate.",
    fields: [
      {
        label: "Max Price ($)",
        help: "Upper price boundary of the tier. The last tier is always infinite (covers all prices above the previous tier).",
      },
      {
        label: "Rate (%)",
        help: "Marginal tax rate for this tier, entered as a decimal — for example 0.04 means 4%.",
      },
      {
        label: "Base Amount",
        help: "Auto-calculated cumulative tax from all previous tiers. You do not need to edit this — it updates whenever Max Price or Rate changes.",
      },
    ],
    tip: "Tier order matters. The system applies tiers in sequence, so Max Price values must be strictly increasing.",
  },
  {
    id: "absd",
    title: "Additional Buyer Stamp Duty (ABSD)",
    description:
      "ABSD is an extra tax on top of BSD, based on the buyer's residency status and how many properties they already own. Rates differ significantly for Singaporeans, PRs, foreigners, and corporate entities.",
    fields: [
      {
        label: "Citizenship",
        help: "The buyer profile: Singaporean, Singapore PR, Foreigner, or Corporate entity.",
      },
      {
        label: "First Property (%)",
        help: "ABSD rate when the buyer is purchasing their first residential property.",
      },
      {
        label: "Second Property (%)",
        help: "ABSD rate when the buyer already owns one property and is buying another.",
      },
      {
        label: "Third+ Properties (%)",
        help: "ABSD rate when the buyer already owns two or more properties.",
      },
    ],
    tip: "These rates are set by Singapore tax policy. Only update them when the government announces a change.",
  },
  {
    id: "ltv",
    title: "Loan-to-Value (LTV) Limits",
    description:
      "LTV limits cap how much a buyer can borrow from a bank, expressed as a percentage of the property price. Limits tighten as the buyer takes on more housing loans, to prevent over-leveraging.",
    fields: [
      {
        label: "Loan Count",
        help: "How many outstanding housing loans the buyer already has: 0, 1, or 2+.",
      },
      {
        label: "LTV (%)",
        help: "Maximum percentage of the property price that can be financed by a bank loan. The remainder must come from cash or CPF.",
      },
    ],
    tip: "Lower LTV = higher cash/CPF required from the buyer. This is a key affordability lever.",
  },
  {
    id: "ssd",
    title: "Seller Stamp Duty (SSD)",
    description:
      "SSD is charged when a property is sold within a few years of purchase. The exact rate depends on how long the seller held the property, when it was bought, and whether it is residential or commercial/industrial.",
    fields: [
      {
        label: "Residential (Before 2025)",
        help: "SSD rates for residential properties bought before the 2025 policy change. The form shows one input per year band (e.g. Years 0-1, Years 1-2, ...). Only the rate (%) is editable; the year bands are fixed.",
      },
      {
        label: "Residential (From 2025)",
        help: "SSD rates for residential properties bought from 2025 onwards. Tighter bands and higher rates than the pre-2025 schedule. One input per year band; only the rate (%) is editable.",
      },
      {
        label: "Commercial / Industrial",
        help: "Separate SSD schedule for non-residential properties. One input per year band; only the rate (%) is editable.",
      },
      {
        label: "Year band (Years X-Y)",
        help: "Holding period range each rate applies to, e.g. 'Years 0-1', 'Years 1-2'. The last bucket is 4+ years and is typically 0%. Year bands are fixed in the schema — only the rate inside each band can be changed.",
      },
    ],
    tip: "Each property falls into exactly one of the three groups above (Before 2025 / From 2025 / Commercial-Industrial). Make sure the rate for the matching group is the one you intend to change.",
  },
];

const CalculatorGuideModal: React.FC<{ initialStepId?: string }> = ({
  initialStepId,
}) => {
  const { closeModal } = useModal();
  const [stepIndex, setStepIndex] = useState(() => {
    if (!initialStepId) return 0;
    const foundIndex = STEPS.findIndex((step) => step.id === initialStepId);
    return foundIndex >= 0 ? foundIndex : 0;
  });

  const currentStep = STEPS[stepIndex];
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === STEPS.length - 1;

  const goNext = () => {
    if (!isLast) setStepIndex(stepIndex + 1);
  };

  const goPrev = () => {
    if (!isFirst) setStepIndex(stepIndex - 1);
  };

  return (
    <div className="p-5 md:p-6 max-w-2xl">
      <div className="flex items-start justify-between mb-4">
        <div>
          <Text className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Guide · Step {stepIndex + 1} of {STEPS.length}
          </Text>
          <h2 className="text-xl md:text-2xl font-bold mt-1">
            {currentStep.title}
          </h2>
        </div>
        <button
          type="button"
          onClick={closeModal}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close guide"
        >
          <PiXBold className="h-5 w-5" />
        </button>
      </div>

      <div className="flex gap-1 mb-5">
        {STEPS.map((step, index) => (
          <div
            key={step.id}
            className={`h-1 flex-1 rounded-full transition-colors ${
              index <= stepIndex ? "bg-primary" : "bg-gray-200"
            }`}
          />
        ))}
      </div>

      <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
        <Text className="text-sm text-gray-700 leading-relaxed">
          {currentStep.description}
        </Text>

        <div className="border rounded-lg divide-y">
          {currentStep.fields.map((field) => (
            <div key={field.label} className="p-3">
              <Text className="text-sm font-semibold">{field.label}</Text>
              <Text className="text-xs text-gray-600 mt-1 leading-relaxed">
                {field.help}
              </Text>
            </div>
          ))}
        </div>

        {currentStep.tip && (
          <div className="border-l-4 border-primary bg-primary/5 px-3 py-2 rounded-r">
            <Text className="text-xs text-gray-700 leading-relaxed">
              <span className="font-semibold">Tip: </span>
              {currentStep.tip}
            </Text>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mt-6 pt-4 border-t">
        <Button
          variant="outline"
          onClick={goPrev}
          disabled={isFirst}
          className="gap-1"
        >
          <PiCaretLeftBold className="h-4 w-4" />
          Back
        </Button>
        {isLast ? (
          <Button onClick={closeModal}>Got it</Button>
        ) : (
          <Button onClick={goNext} className="gap-1">
            Next
            <PiCaretRightBold className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default CalculatorGuideModal;
